import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FC, Fragment, ReactNode, useEffect, useState } from 'react';

import { deleteLeadCampaign, fetchLeadCampaignRecipients, fetchLeadCampaigns } from '../../api/leads';
import { LeadCampaignRecipientSchema, LeadCampaignSchema } from '../../data/types';
import {
  buildCampaignIssueStats,
  buildCampaignPrimaryKpis,
  campaignDeleteConfirmMessage,
  campaignStatusColor,
  canDeleteCampaign,
  canEditCampaign,
  canResendCampaign,
  formatLeadFriendlyTimestamp,
  formatLeadFullTimestamp,
  isTerminalCampaignStatus,
  labelForCampaignStatus,
  LEAD_CAMPAIGN_STATUS_OPTIONS,
  LEAD_DEFAULT_TIMEZONE,
} from '../../utils/leadsAdmin';
import { LeadConfirmModal } from './LeadConfirmModal';
import { LeadMergeTagText } from './LeadMergeTagText';

type LeadCampaignPanelProps = {
  adminEmail: string;
  campaigns: LeadCampaignSchema[];
  loading?: boolean;
  loadingMore?: boolean;
  statusFilter?: string;
  hasMore?: boolean;
  showingCount?: number;
  totalCount?: number;
  /** Community timezone used to render every campaign timestamp. */
  timezone?: string;
  onRefresh: () => void;
  onStatusFilterChange?: (status: string) => void;
  onSeeMore?: () => void;
  onEdit: (campaign: LeadCampaignSchema) => void;
  onDuplicate: (campaign: LeadCampaignSchema) => void;
  onResend: (campaign: LeadCampaignSchema) => void;
};

// Restrained ink/slate surface tokens with a single deep-teal accent. Kept as literal
// class strings so Tailwind can statically extract them.
const COLLAPSED_CARD_CLASS =
  'overflow-hidden rounded-xl border border-[#E4E9EB] bg-white shadow-[0_1px_2px_rgba(11,46,56,0.04)] transition-colors hover:border-primary-100/30';
const EXPANDED_CARD_CLASS =
  'overflow-hidden rounded-xl border border-primary-100/40 border-l-2 border-l-primary-100 bg-white shadow-[0_6px_16px_rgba(1,56,71,0.10)]';
const PANEL_SURFACE_CLASS =
  'space-y-4 border-t border-primary-100/15 bg-primary-tint/60 px-4 py-4 font-open-sans text-xs text-[#5F7078]';
const CHIP_CLASS =
  'inline-flex items-center gap-1.5 rounded-full border border-[#E4E9EB] px-2.5 py-0.5 text-[11px] font-raleway text-[#5F7078]';
const HERO_CARD_CLASS =
  'rounded-lg border border-[#E4E9EB] border-t-2 border-t-primary-100 bg-white px-3 py-3';
const META_TEXT_CLASS = 'inline-flex items-center gap-1.5 text-[#5F7078]';
const ICON_MUTED_CLASS = 'text-[#8A9AA1]';
const GHOST_BUTTON_CLASS =
  'rounded-lg border border-[#E4E9EB] px-3 py-1.5 text-xs font-semibold font-raleway text-primary-0 transition-colors hover:bg-primary-tint disabled:opacity-60';

type CampaignMetaItem = {
  key: string;
  icon: ReactNode;
  text: string;
  raw?: string;
  iconClass: string;
};

const KPI_ICONS: Record<string, ReactNode> = {
  delivered: <SendIcon sx={{ fontSize: 13 }} />,
  open_rate: <VisibilityIcon sx={{ fontSize: 13 }} />,
  click_rate: <TouchAppIcon sx={{ fontSize: 13 }} />,
  clicks: <TouchAppIcon sx={{ fontSize: 13 }} />,
  replies: <ReplyIcon sx={{ fontSize: 13 }} />,
};

/** Neutral status pill with a small colored state dot. */
const CampaignStatusBadge: FC<{ status?: string }> = ({ status }) => {
  const colors = campaignStatusColor(status);
  const isSending = status === 'sending';
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F4F6F7] px-2.5 py-0.5 text-[11px] font-semibold font-raleway text-[#3F5158]">
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${isSending ? 'animate-pulse' : ''}`}
        style={{ backgroundColor: colors.primary }}
      />
      {labelForCampaignStatus(status)}
    </span>
  );
};

/** Dot color for a recipient delivery status pill. */
const recipientStatusDotColor = (status?: string): string => {
  switch (status) {
    case 'delivered':
    case 'sent':
      return '#2F6B4F';
    case 'failed':
    case 'bounced':
      return '#A63D3D';
    case 'simulated':
      return '#3C6B78';
    default:
      return '#8A9AA1';
  }
};

/** Empty-state copy for the active campaign status filter. */
const emptyCampaignsMessage = (statusFilter?: string): string => {
  if (!statusFilter) return 'No campaigns yet. Create one to get started.';
  const label = labelForCampaignStatus(statusFilter).toLowerCase();
  return `No ${label} campaigns.`;
};

/** Lists draft and sent campaigns with delivery tracking. */
export const LeadCampaignPanel: FC<LeadCampaignPanelProps> = ({
  adminEmail,
  campaigns,
  loading = false,
  loadingMore = false,
  statusFilter = '',
  hasMore = false,
  showingCount,
  totalCount,
  timezone = LEAD_DEFAULT_TIMEZONE,
  onRefresh,
  onStatusFilterChange,
  onSeeMore,
  onEdit,
  onDuplicate,
  onResend,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeadCampaignSchema | null>(null);
  const [recipientsByCampaign, setRecipientsByCampaign] = useState<
    Record<string, LeadCampaignRecipientSchema[]>
  >({});
  const [recipientsLoadingId, setRecipientsLoadingId] = useState<string | null>(null);

  useEffect(() => {
    if (!expandedId || !adminEmail) return;
    if (recipientsByCampaign[expandedId]) return;
    setRecipientsLoadingId(expandedId);
    fetchLeadCampaignRecipients(expandedId, adminEmail, { limit: 25 })
      .then((data) => {
        setRecipientsByCampaign((current) => ({
          ...current,
          [expandedId]: data?.items ?? [],
        }));
      })
      .finally(() => setRecipientsLoadingId(null));
  }, [adminEmail, expandedId, recipientsByCampaign]);

  const handleDeleteRequest = (campaign: LeadCampaignSchema) => {
    if (!campaign.id || !canDeleteCampaign(campaign)) return;
    setDeleteTarget(campaign);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeletingId(deleteTarget.id);
    try {
      await deleteLeadCampaign(deleteTarget.id, adminEmail);
      setDeleteTarget(null);
      onRefresh();
    } finally {
      setDeletingId(null);
    }
  };

  const shown = showingCount ?? campaigns.length;
  const total = totalCount ?? campaigns.length;

  const statusTabClass = (selected: boolean) =>
    `rounded-full px-3 py-1 text-xs font-semibold font-raleway transition-colors ${
      selected ? 'bg-primary-100 text-white' : 'text-[#5F7078] hover:bg-primary-tint'
    }`;

  return (
    <div className="rounded-xl border border-[#E4E9EB] bg-white p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold font-raleway text-primary-0">Campaigns</h2>
          <p className="text-xs text-[#5F7078] font-open-sans">
            Drafts, sends in progress, delivery results, and engagement history.
          </p>
        </div>
        <button type="button" disabled={loading} className={GHOST_BUTTON_CLASS} onClick={onRefresh}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-1" role="tablist" aria-label="Filter campaigns by status">
        <button
          type="button"
          role="tab"
          aria-selected={!statusFilter}
          className={statusTabClass(!statusFilter)}
          onClick={() => onStatusFilterChange?.('')}
        >
          All
        </button>
        {LEAD_CAMPAIGN_STATUS_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={statusFilter === option.value}
            className={statusTabClass(statusFilter === option.value)}
            onClick={() => onStatusFilterChange?.(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {campaigns.length === 0 ? (
        <p className="text-sm text-[#5F7078] font-open-sans">{emptyCampaignsMessage(statusFilter)}</p>
      ) : (
        <ul className="space-y-3">
          {campaigns.map((campaign) => {
            const expanded = expandedId === campaign.id;
            const stats = campaign.delivery_stats;
            const engagement = campaign.engagement_stats;
            const kpis = buildCampaignPrimaryKpis(stats, engagement);
            const issues = buildCampaignIssueStats(stats, engagement);
            const progressPct =
              stats?.requested && stats.requested > 0
                ? Math.round(((stats.sent ?? 0) + (stats.failed ?? 0) + (stats.simulated ?? 0)) / stats.requested * 100)
                : 0;
            const recipients = campaign.id ? recipientsByCampaign[campaign.id] ?? [] : [];
            const deletable = canDeleteCampaign(campaign);
            const collapsedStamp = campaign.completed_at || campaign.sent_at || campaign.created;
            const collapsedStampIcon = campaign.completed_at ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
            ) : campaign.sent_at ? (
              <SendIcon sx={{ fontSize: 14 }} />
            ) : (
              <CalendarTodayIcon sx={{ fontSize: 14 }} />
            );
            const collapsedStampClass = campaign.completed_at
              ? 'text-[#2F6B4F]'
              : campaign.sent_at
                ? 'text-[#013847]'
                : 'text-[#8A9AA1]';
            const metaItems: Array<CampaignMetaItem | null> = [
              {
                key: 'created',
                icon: <CalendarTodayIcon sx={{ fontSize: 13 }} />,
                text: formatLeadFriendlyTimestamp(campaign.created, timezone),
                raw: formatLeadFullTimestamp(campaign.created, timezone),
                iconClass: 'text-[#8A9AA1]',
              },
              campaign.sent_at
                ? {
                    key: 'sent',
                    icon: <SendIcon sx={{ fontSize: 13 }} />,
                    text: formatLeadFriendlyTimestamp(campaign.sent_at, timezone),
                    raw: formatLeadFullTimestamp(campaign.sent_at, timezone),
                    iconClass: 'text-[#013847]',
                  }
                : null,
              campaign.completed_at
                ? {
                    key: 'completed',
                    icon: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />,
                    text: formatLeadFriendlyTimestamp(campaign.completed_at, timezone),
                    raw: formatLeadFullTimestamp(campaign.completed_at, timezone),
                    iconClass: 'text-[#2F6B4F]',
                  }
                : null,
              campaign.recipient_count != null
                ? {
                    key: 'recipients',
                    icon: <PeopleOutlineIcon sx={{ fontSize: 13 }} />,
                    text: `${campaign.recipient_count}`,
                    iconClass: 'text-[#8A9AA1]',
                  }
                : null,
            ];
            const visibleMeta = metaItems.filter((item): item is CampaignMetaItem => item !== null);

            return (
              <li key={campaign.id} className={expanded ? EXPANDED_CARD_CLASS : COLLAPSED_CARD_CLASS}>
                <div className={`flex flex-wrap items-start justify-between gap-3 px-4 py-3 ${expanded ? 'bg-primary-tint' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-raleway text-sm font-semibold text-primary-0">
                        <LeadMergeTagText value={campaign.name} fallback="Untitled" />
                      </p>
                      <CampaignStatusBadge status={campaign.status} />
                    </div>
                    <p className="mt-1 truncate font-open-sans text-xs text-[#5F7078]">
                      <LeadMergeTagText value={campaign.subject} fallback="No subject" />
                    </p>
                    {collapsedStamp ? (
                      <p className="mt-1.5 inline-flex items-center gap-1 text-xs font-open-sans text-[#5F7078]">
                        <span className={collapsedStampClass}>{collapsedStampIcon}</span>
                        <span title={formatLeadFullTimestamp(collapsedStamp, timezone) || undefined}>
                          {formatLeadFriendlyTimestamp(collapsedStamp, timezone)}
                        </span>
                      </p>
                    ) : null}
                    {campaign.status === 'sending' ? (
                      <div className="mt-2">
                        <div className="h-1 w-full max-w-xs overflow-hidden rounded-full bg-[#EDF1F2]">
                          <div
                            className="h-full rounded-full bg-primary-100 transition-all"
                            style={{ width: `${Math.min(progressPct, 100)}%` }}
                          />
                        </div>
                        <p className="mt-1 text-xs text-[#5F7078]">
                          Sending… {stats?.sent ?? 0}/{stats?.requested ?? 0}
                        </p>
                      </div>
                    ) : isTerminalCampaignStatus(campaign.status) ? (
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {kpis.collapsed.map((item) => (
                          <span key={item.key} className={CHIP_CLASS}>
                            <span className={ICON_MUTED_CLASS}>{KPI_ICONS[item.key]}</span>
                            {item.label} <span className="font-semibold text-primary-0">{item.value}</span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-0.5">
                    {canEditCampaign(campaign.status) ? (
                      <button
                        type="button"
                        aria-label={`Edit ${campaign.name}`}
                        className="rounded-md p-2 text-[#5F7078] transition-colors hover:bg-[#F4F6F7] hover:text-[#0B2E38]"
                        onClick={() => onEdit(campaign)}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </button>
                    ) : null}
                    {canResendCampaign(campaign.status) ? (
                      <button
                        type="button"
                        aria-label={`Resend failed recipients for ${campaign.name}`}
                        className="rounded-md p-2 text-[#5F7078] transition-colors hover:bg-[#F4F6F7] hover:text-[#013847]"
                        onClick={() => onResend(campaign)}
                      >
                        <SendIcon sx={{ fontSize: 18 }} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      aria-label={`Duplicate ${campaign.name}`}
                      className="rounded-md p-2 text-[#5F7078] transition-colors hover:bg-[#F4F6F7] hover:text-[#0B2E38]"
                      onClick={() => onDuplicate(campaign)}
                    >
                      <ContentCopyIcon sx={{ fontSize: 18 }} />
                    </button>
                    {deletable ? (
                      <button
                        type="button"
                        aria-label={`Delete ${campaign.name}`}
                        disabled={deletingId === campaign.id}
                        className="rounded-md p-2 text-[#A63D3D] transition-colors hover:bg-[#FBF1F1] disabled:opacity-50"
                        onClick={() => handleDeleteRequest(campaign)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled
                        aria-label="Sent campaigns cannot be deleted"
                        title="Sent campaigns cannot be deleted"
                        className="cursor-not-allowed rounded-md p-2 text-[#C4CDD1]"
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label={expanded ? 'Collapse details' : 'Expand details'}
                      className={`rounded-md p-2 transition-colors ${
                        expanded
                          ? 'bg-primary-100 text-white hover:bg-primary-0'
                          : 'text-[#5F7078] hover:bg-primary-tint'
                      }`}
                      onClick={() => setExpandedId(expanded ? null : campaign.id ?? null)}
                    >
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 20,
                          transform: expanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </button>
                  </div>
                </div>

                {expanded ? (
                  <div className={PANEL_SURFACE_CLASS}>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {visibleMeta.map((item, index) => (
                        <Fragment key={item.key}>
                          {index > 0 ? (
                            <span aria-hidden="true" className="text-[#C4CDD1]">
                              ·
                            </span>
                          ) : null}
                          <span className={META_TEXT_CLASS} title={item.raw || undefined}>
                            <span className={item.iconClass}>{item.icon}</span>
                            {item.text}
                          </span>
                        </Fragment>
                      ))}
                    </div>
                    {isTerminalCampaignStatus(campaign.status) ? (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {kpis.hero.map((item) => (
                          <div key={item.key} className={HERO_CARD_CLASS}>
                            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8A9AA1]">
                              <span className={ICON_MUTED_CLASS}>{KPI_ICONS[item.key]}</span>
                              {item.label}
                            </p>
                            <p className="mt-1.5 text-xl font-bold font-raleway text-primary-0">{item.value}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {issues.length > 0 ? (
                      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#F0E4CE] bg-[#FBF6EE] px-3 py-2 text-[#8A5A1E]">
                        <WarningAmberIcon sx={{ fontSize: 15 }} />
                        {issues.map((item) => (
                          <span key={item.label} className="font-semibold">
                            {item.value} {item.label.toLowerCase()}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {campaign.audience?.group_name ? (
                      <p>Audience: group "{campaign.audience.group_name}"</p>
                    ) : campaign.audience?.type === 'lead_ids' ? (
                      <p>Audience: {campaign.audience.lead_ids?.length ?? 0} manually selected lead(s)</p>
                    ) : (
                      <p>Audience: filtered lead list</p>
                    )}
                    {(campaign.failures?.length ?? 0) > 0 ? (
                      <div>
                        <p className="font-semibold text-[#A63D3D]">Failures</p>
                        <ul className="mt-1 space-y-1">
                          {campaign.failures?.slice(0, 10).map((failure) => (
                            <li key={`${failure.lead_id}-${failure.email}`}>
                              {failure.email || failure.lead_id}: {failure.error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {isTerminalCampaignStatus(campaign.status) ? (
                      <div>
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8A9AA1]">
                          Recipient history
                        </p>
                        {recipientsLoadingId === campaign.id ? (
                          <p>Loading recipients…</p>
                        ) : recipients.length === 0 ? (
                          <p>No recipient records yet.</p>
                        ) : (
                          <div className="max-h-64 overflow-auto rounded-lg border border-[#E4E9EB] bg-white">
                            <table className="min-w-full text-left text-xs">
                              <thead className="sticky top-0 bg-primary-tint">
                                <tr className="border-b border-primary-100/15">
                                  <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary-200/70">
                                    Recipient
                                  </th>
                                  <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary-200/70">
                                    Status
                                  </th>
                                  <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary-200/70">
                                    Opened
                                  </th>
                                  <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary-200/70">
                                    Clicked
                                  </th>
                                  <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary-200/70">
                                    Replied
                                  </th>
                                  <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary-200/70">
                                    Sent
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {recipients.map((recipient) => (
                                  <tr
                                    key={recipient.id ?? `${recipient.lead_id}-${recipient.email}`}
                                    className="border-t border-[#F2F5F6] transition-colors hover:bg-[#FAFBFB]"
                                  >
                                    <td className="px-3 py-2">
                                      <div className="font-medium text-[#0B2E38]">
                                        {recipient.business_name || recipient.first_name || recipient.email || '—'}
                                      </div>
                                      <div className="text-[#8A9AA1]">{recipient.email || '—'}</div>
                                      {recipient.error ? (
                                        <div className="mt-0.5 text-[#A63D3D]">{recipient.error}</div>
                                      ) : null}
                                    </td>
                                    <td className="px-3 py-2">
                                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F4F6F7] px-2 py-0.5 text-[11px] font-medium text-[#3F5158]">
                                        <span
                                          aria-hidden="true"
                                          className="h-1.5 w-1.5 rounded-full"
                                          style={{ backgroundColor: recipientStatusDotColor(recipient.status) }}
                                        />
                                        {recipient.status || '—'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2">
                                      {recipient.opened ? (
                                        <span className="inline-flex items-center gap-0.5 text-[#3C6B78]">
                                          <CheckIcon sx={{ fontSize: 14 }} />
                                          {recipient.open_count ?? 1}
                                        </span>
                                      ) : (
                                        <span className="text-[#C4CDD1]">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      {recipient.clicked ? (
                                        <span className="inline-flex items-center gap-0.5 text-[#3C6B78]">
                                          <CheckIcon sx={{ fontSize: 14 }} />
                                          {recipient.click_count ?? 1}
                                        </span>
                                      ) : (
                                        <span className="text-[#C4CDD1]">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      {recipient.replied ? (
                                        <span className="inline-flex items-center gap-0.5 text-[#3C6B78]">
                                          <CheckIcon sx={{ fontSize: 14 }} />
                                          {recipient.reply_count ?? 1}
                                        </span>
                                      ) : (
                                        <span className="text-[#C4CDD1]">—</span>
                                      )}
                                    </td>
                                    <td
                                      className="px-3 py-2 text-[#5F7078]"
                                      title={formatLeadFullTimestamp(recipient.sent_at, timezone) || undefined}
                                    >
                                      {formatLeadFriendlyTimestamp(recipient.sent_at, timezone)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {campaigns.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-[#8A9AA1] font-open-sans">
            Showing {shown} of {total}
          </p>
          {hasMore ? (
            <button type="button" disabled={loadingMore} className={GHOST_BUTTON_CLASS} onClick={onSeeMore}>
              {loadingMore ? 'Loading…' : 'See more'}
            </button>
          ) : null}
        </div>
      ) : null}

      <LeadConfirmModal
        isOpen={Boolean(deleteTarget)}
        title="Delete campaign?"
        message={campaignDeleteConfirmMessage(deleteTarget ?? undefined)}
        confirmLabel="Delete"
        confirmTone="danger"
        submitting={Boolean(deletingId)}
        onClose={() => {
          if (deletingId) return;
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

/** Load campaigns for the panel (exported for tests). */
export const loadLeadCampaignPanelData = fetchLeadCampaigns;
